import { useState, useCallback, useEffect, useRef } from "react"

import { useElementBase, ElementAdapter } from "./useElementBase"
import { useTableConfig } from "./useElementConfigFactory"
import { useOperatorManagement } from "../useOperatorManagement"

import {
  DEFAULT_TABLE_WIDTH,
  DEFAULT_TABLE_HEIGHT,
} from "constants/dashboardElementSizes"

import { ScriptGenerationService } from "services/ScriptGenerationService"

import {
  STATISTIC_TYPE,
  SlideElement,
  DataElementConfig,
  TableElementSettings,
  TABLE_OPERATION_TYPE,
  ELEMENT_TYPE,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import {
  EngineQueryResult,
  EngineOutputItem,
} from "types/engineOutput.types"
import { ElementRef } from "../../../../features/Reporting/components/ReportingDisplay/ReportingDisplay.component"

/**
 * Element update payload for table elements
 */
interface TableElementUpdate {
  width?: number
  height?: number
}

/**
 * Props for useTableElement hook
 */
interface UseTableElementProps {
  /** Global filter string passed to data elements */
  filter?: string
  /** Element object to render */
  element: SlideElement
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Callback when element properties change */
  onUpdate: (elementId: string, updates: TableElementUpdate) => void
  /** Configuration for table element */
  config?: DataElementConfig | null
  /** Callback for config updates */
  onConfigUpdate: (config: DataElementConfig | null) => void
  /** Callback when element deletion is requested */
  onDeleteRequest?: () => void
  /** Callback for updating runtime cache */
  onTableDataUpdate?: (
    elementId: string,
    data: EngineQueryResult | null
  ) => void
  /** Sequential execution request handler */
  onElementExecutionRequest?: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void
  /** Element registration handler */
  onRegisterElementRef?: (elementId: string, ref: ElementRef | null) => void
}

/**
 * Default operator options for tables
 */
const DEFAULT_OPERATORS: STATISTIC_TYPE[] = [STATISTIC_TYPE.N]

/**
 * Table size state
 */
interface TableSize {
  width: number
  height: number
}

/**
 * Custom Hook: useTableElement
 *
 * Table element business logic, composed from useElementBase (shared
 * lifecycle: data loading, config wiring, element state, auto-run,
 * filter-change and registration effects, refresh/edit/delete family)
 * plus the table adapter (script generation with transpose, config-prop
 * variable fallback, runtime-cache updates) and table-only extras: the
 * measurement/resize machinery, the explicit 700ms config-save effect
 * (Table's persistence path — unlike the siblings' image effects) and the
 * transpose handler.
 *
 * Pinned table quirks preserved: markAttemptedLoad runs BEFORE variable
 * validation (unlike Pie), resetSelection double-fires onConfigUpdate(null)
 * (risk item 5), and the executor's redundant setQueuedForExecution(false)
 * (risk item 12) is kept verbatim.
 *
 * @param props - Table element configuration and callbacks
 * @returns Object containing all state, handlers, and configuration needed by component
 */
export const useTableElement = (props: UseTableElementProps) => {
  const { element, onUpdate, onConfigUpdate, onTableDataUpdate } = props

  // Keep table-specific UI state (not saved to config)
  const [tableSize, setTableSize] = useState<TableSize>({
    width: 0,
    height: 0,
  })
  const [isResizingTable, setIsResizingTable] = useState<boolean>(false)
  const [measurementTaken, setMeasurementTaken] = useState<boolean>(false)

  // Refs (created before the adapter so its callbacks can capture them)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const originalWidthRef = useRef<number | null>(null)
  /**
   * Latest transpose value. Synced from config state on every render AND
   * written synchronously by handleToggleTranspose, so the executor
   * provably sees the flipped value even when the sequential queue drains
   * before React re-renders (2026-07-02 decision, risk item 4).
   */
  const isTransposedRef = useRef<boolean>(false)

  const adapter: ElementAdapter<EngineOutputItem> = {
    elementType: ELEMENT_TYPE.TABLE,
    isTimeSeries: false,
    useConfigHook: useTableConfig,
    defaultSize: { width: DEFAULT_TABLE_WIDTH, height: DEFAULT_TABLE_HEIGHT },
    reactsToFilter: true,
    internalAutoRunEnabled: true,
    internalAutoRunConditions: (ctx): boolean => {
      const crossTabVariables = ctx.getValue<(string | null)[]>(
        "crossTabVariables",
        []
      )
      const hasVariables = crossTabVariables.length === 2
      return (
        hasVariables &&
        !ctx.showSelects &&
        !!ctx.queryInstance &&
        !ctx.configHook.isInitializing
      )
    },
    convertsRTableData: true,
    configShowsData: (config): boolean =>
      (config as TableElementSettings).crossTabVariables?.length === 2,
    resetDataForConfigLoad: (ctx): void => {
      ctx.setData(null)
    },
    onConfigLoaded: (ctx, config): void => {
      originalWidthRef.current = config
        ? (config as TableElementSettings).originalWidth ?? null
        : null
    },
    hasRequiredSelections: (ctx): boolean =>
      ctx.getValue<(string | null)[]>("crossTabVariables", []).length === 2,
    autoRunSignature: (ctx): string =>
      JSON.stringify(ctx.getValue<(string | null)[]>("crossTabVariables", [])),
    hasDataAndShouldRefresh: (ctx): boolean =>
      !!(
        ctx.getValue<(string | null)[]>("crossTabVariables", []).length ===
          2 && ctx.data
      ),
    /**
     * Main script execution function (called by the sequential executor).
     * CURRENT BEHAVIOR (pinned): markAttemptedLoad runs BEFORE the variable
     * validation, so hasAttemptedLoad flips true even when nothing runs.
     */
    execute: async (ctx): Promise<void> => {
      if (ctx.isLoading) return
      ctx.clearError()
      ctx.markAttemptedLoad()
      ctx.setQueuedForExecution(false)

      const crossTabVariables = ctx.getValue<(string | null)[]>(
        "crossTabVariables",
        []
      )
      const selectedOperators = ctx.getValue<STATISTIC_TYPE[]>(
        "selectedOperators",
        DEFAULT_OPERATORS
      )
      const tableOperationType = ctx.getValue<TABLE_OPERATION_TYPE>(
        "tableOperationType",
        TABLE_OPERATION_TYPE.XTAB
      )
      // Read the transpose value through the ref (see isTransposedRef docs)
      // so a queued transpose run sees the flipped value.
      const transposed = isTransposedRef.current

      const varsToUse =
        crossTabVariables.length === 2
          ? crossTabVariables
          : (ctx.props.config as TableElementSettings)?.crossTabVariables || []

      if (varsToUse.length !== 2 || !varsToUse[0] || !varsToUse[1]) {
        console.warn("Two cross tab variables are required")
        return
      }

      try {
        // Generate script using the centralized service
        const script = ScriptGenerationService.generateTableScript(
          ctx.element.id,
          [varsToUse[0], varsToUse[1]] as [string, string],
          selectedOperators,
          tableOperationType,
          ctx.filter,
          transposed
        )

        // Update config values during execution
        ctx.configHook.setConfigValue("isTransposed", transposed)
        if (varsToUse !== crossTabVariables) {
          ctx.configHook.setConfigValue("crossTabVariables", varsToUse)
        }
        const result = await ctx.executeScript(script, ctx.element.id)

        if (result.success) {
          // The hook returns an EngineOutputItem directly
          const engineOutputItem = result.data as EngineOutputItem
          ctx.setData(engineOutputItem)
          ctx.updateConfigState({ showSelects: false })

          // Update runtime cache with table data - wrap in EngineQueryResult for cache
          if (onTableDataUpdate) {
            const wrappedResult: EngineQueryResult = {
              result: {
                output: [engineOutputItem],
              },
            }
            onTableDataUpdate(ctx.element.id, wrappedResult)
          }
        }
      } catch (e) {
        console.error(
          `Script execution error for element ${ctx.element.id}:`,
          e
        )
      }
    },
    resetState: (ctx): void => {
      ctx.setData(null)
      ctx.updateConfigState({ showSelects: true })
      ctx.clearError()
      ctx.updateElementState({
        hasAttemptedLoad: false,
        isQueuedForExecution: false,
      })
      originalWidthRef.current = null
    },
    onAfterReset: (ctx): void => {
      // CURRENT BEHAVIOR (risk item 5, pinned): resetSelection double-fires
      // onConfigUpdate(null) — once via configHook.clearConfig() (in the
      // base) and once directly here.
      if (onConfigUpdate) onConfigUpdate(null)

      if (onTableDataUpdate) {
        onTableDataUpdate(ctx.element.id, null)
      }
    },
    onAfterDelete: (ctx): void => {
      if (onTableDataUpdate) {
        onTableDataUpdate(ctx.element.id, null)
      }
    },
  }

  const base = useElementBase<EngineOutputItem>(props, adapter)

  const {
    isLoading,
    error,
    configHook,
    data: tableData,
    showSelects,
    hasAttemptedLoad,
    isQueuedForExecution,
    convertedData: convertedTableData,
    selectOptions,
    requestSequentialExecution,
    hasDataAndShouldRefresh,
    handleRefresh,
    handleEdit,
    handleDelete,
  } = base

  // Get values from config with fallbacks (matching original defaults)
  const selectedOperators = configHook.getConfigValue<STATISTIC_TYPE[]>(
    "selectedOperators",
    DEFAULT_OPERATORS
  )
  const crossTabVariables = configHook.getConfigValue<(string | null)[]>(
    "crossTabVariables",
    []
  )
  const isTransposed = configHook.getConfigValue<boolean>("isTransposed", false)
  const tableOperationType = configHook.getConfigValue<TABLE_OPERATION_TYPE>(
    "tableOperationType",
    TABLE_OPERATION_TYPE.XTAB
  )

  // Keep the executor's transpose view in sync with config state.
  isTransposedRef.current = isTransposed

  // Use operator management hook for centralized operator handling
  const operatorManagement = useOperatorManagement({
    initialOperationType: tableOperationType,
    initialOperators: selectedOperators,
    singleStatisticMode: false, // Tables use multiple operator selection
    onOperationTypeChange: (newOperationType) => {
      configHook.setConfigValue("tableOperationType", newOperationType)
    },
    onOperatorsChange: (newOperators) => {
      configHook.setConfigValue("selectedOperators", newOperators)
    },
  })

  const handleToggleTranspose = useCallback((): void => {
    const newTransposed = !isTransposed
    setMeasurementTaken(false)
    setTableSize({ width: 0, height: 0 })

    if (!newTransposed && originalWidthRef.current !== null) {
      setIsResizingTable(true)
      const originalWidth = originalWidthRef.current
      setTimeout(() => {
        onUpdate(element.id, { width: originalWidth })
        originalWidthRef.current = null
        setIsResizingTable(false)
      }, 50)
    }

    // DECIDED (2026-07-02, design doc risk item 4): route the transpose run
    // through the sequential queue instead of calling the executor directly
    // (the direct call could race a queued run). The flipped value is
    // written to config AND to isTransposedRef synchronously, so the queued
    // executor provably sees it even if the parent drains the queue before
    // React state settles.
    configHook.setConfigValue("isTransposed", newTransposed)
    isTransposedRef.current = newTransposed
    configHook.setSkipNextConfigUpdate(true)
    requestSequentialExecution("transpose_toggle")
  }, [
    isTransposed,
    onUpdate,
    element.id,
    configHook,
    requestSequentialExecution,
  ])

  const handleVariableSelect = useCallback(
    (value: string, index: number): void => {
      const newVariables = [...crossTabVariables]
      newVariables[index] = value
      configHook.setConfigValue("crossTabVariables", newVariables)
    },
    [crossTabVariables, configHook]
  )

  const handleOperatorChange = operatorManagement.handleOperatorsChange

  const handleTableOperationTypeChange =
    operatorManagement.handleOperationTypeChange

  // Generate handler with config management
  const handleGenerate = useCallback(() => {
    configHook.setSkipNextConfigUpdate(false)
    requestSequentialExecution("user_run")
  }, [configHook, requestSequentialExecution])

  // Table measurement effect
  /**
   * Intelligent Table Measurement System
   *
   * Automatically measures table content and adjusts container size:
   * tracks table scroll dimensions, applies a >2% change threshold, and
   * coordinates with the resize system to avoid conflicting measurements.
   */
  useEffect(() => {
    if (tableContainerRef.current && tableData) {
      const measureTable = (): void => {
        if (isResizingTable) return
        const tableElement = tableContainerRef.current?.querySelector("table")
        if (tableElement) {
          const newWidth = tableElement.scrollWidth
          const newHeight = tableElement.scrollHeight
          const currentWidth = tableSize.width
          const currentHeight = tableSize.height
          const widthChange =
            Math.abs(newWidth - currentWidth) / (currentWidth || newWidth)
          const heightChange =
            Math.abs(newHeight - currentHeight) / (currentHeight || newHeight)

          if (
            currentWidth === 0 ||
            currentHeight === 0 ||
            widthChange > 0.02 ||
            heightChange > 0.02
          ) {
            setTableSize({ width: newWidth, height: newHeight })
          }
        }
      }
      if (!isResizingTable) {
        measureTable()
        setMeasurementTaken(true)
      }
    }
  }, [
    tableData,
    isTransposed,
    isResizingTable,
    tableSize.width,
    tableSize.height,
  ])

  // Table resize effect
  /**
   * Dynamic Table Resizing System
   *
   * Automatically adjusts container width based on measured table content.
   * Only applies to transposed tables; preserves the original width for
   * restore operations and debounces resize completion (500ms).
   */
  useEffect(() => {
    if (
      tableSize.width > 0 &&
      isTransposed &&
      !showSelects &&
      !isResizingTable &&
      measurementTaken
    ) {
      if (originalWidthRef.current === null) {
        originalWidthRef.current = element.width || DEFAULT_TABLE_WIDTH
      }

      const currentWidth = element.width || DEFAULT_TABLE_WIDTH
      if (tableSize.width > currentWidth - 40) {
        setIsResizingTable(true)
        const newWidth = tableSize.width + 40
        if (Math.abs(newWidth - currentWidth) / currentWidth > 0.05) {
          onUpdate(element.id, { width: newWidth })
        }
        setTimeout(() => {
          setIsResizingTable(false)
        }, 500)
      }
    }
  }, [
    tableSize,
    isTransposed,
    measurementTaken,
    element.width,
    showSelects,
    isResizingTable,
    onUpdate,
    element.id,
  ])

  // Config saving effect
  /**
   * Configuration Persistence System (Table's persistence path — the
   * siblings persist from their image effects instead; risk item 9
   * behavior intentionally unchanged).
   *
   * Debounced (700ms) save of crossTabVariables/selectedOperators/
   * isTransposed/originalWidth/tableOperationType when data is displayed;
   * honors the skipNextConfigUpdate flag and coordinates with the
   * isGeneratingImageRef guard used by the config-loading effect.
   * Note: table data itself is NOT saved to config.
   */
  useEffect(() => {
    if (tableData && tableContainerRef.current && !showSelects) {
      const timerId = setTimeout(async () => {
        if (!tableContainerRef.current) return
        try {
          configHook.isGeneratingImageRef.current = true
          if (onConfigUpdate && !configHook.skipNextConfigUpdate) {
            onConfigUpdate({
              id: element.id,
              crossTabVariables: crossTabVariables,
              selectedOperators: selectedOperators,
              isTransposed: isTransposed,
              originalWidth: originalWidthRef.current,
              tableOperationType: tableOperationType,
            } as TableElementSettings)
          } else if (configHook.skipNextConfigUpdate) {
            configHook.setSkipNextConfigUpdate(false)
          }
          setTimeout(() => {
            configHook.isGeneratingImageRef.current = false
          }, 100)
        } catch (error) {
          console.error(
            `Failed to update config for element: ${element.id}`,
            error
          )
          if (configHook.skipNextConfigUpdate) {
            configHook.setSkipNextConfigUpdate(false)
          }
          setTimeout(() => {
            configHook.isGeneratingImageRef.current = false
          }, 100)
        }
      }, 700)

      return () => clearTimeout(timerId)
    }
  }, [
    tableData,
    showSelects,
    element.id,
    onConfigUpdate,
    configHook.skipNextConfigUpdate,
    crossTabVariables,
    selectedOperators,
    isTransposed,
    tableOperationType,
    configHook,
  ])

  // Return all the state and handlers needed by the component
  return {
    // State
    tableData,
    showSelects,
    isLoading,
    isQueuedForExecution,
    error,
    hasAttemptedLoad,
    convertedTableData,
    tableSize,
    isResizingTable,
    measurementTaken,
    selectOptions,

    // Config values
    selectedOperators,
    crossTabVariables,
    isTransposed,
    tableOperationType,

    // Hooks
    configHook,
    operatorManagement,

    // Handlers
    handleRefresh,
    handleEdit,
    handleDelete,
    handleToggleTranspose,
    handleVariableSelect,
    handleOperatorChange,
    handleTableOperationTypeChange,
    handleGenerate,
    requestSequentialExecution,

    // Imperative handle values
    hasDataAndShouldRefresh,

    // Refs and utilities
    tableContainerRef,
  }
}
